import { get } from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { pages } from '../../../states/page/actions';
import MakeMatch from '../MakeMatch/MakeMatch';
import CloseMatch from '../CloseMatch/CloseMatch';
import AccountWarning from '../AccountWarning/AccountWarning';
import copPropTypes from '../../states/propTypes';
import NoMatch from '../NoMatch/NoMatch';
import { setCopDetails } from '../../states/actions';
import { getCopPage, getCopResult } from '../../states/selectors';
import { getAccountError, getTemplateError } from '../../../states/payees/selectors';
import Blocked from '../NoMatch/Blocked';
import ServiceDown from '../NoMatch/ServiceDown';
import { CreateAccountError } from '../CreateAccountError/CreateAccountError';
import { CreateTemplateError } from '../CreateTemplateError';
import { MatchMade } from '../MatchMade/MatchMade';
import { COP_MAX_TRY_COUNT } from '../../../config/';
import { returnBlockedPage, checkIfMatchFailed } from '../../../helpers/copHelpers';

export class LayoutContainer extends Component {
	constructor(props) {
		super(props);

		this.setKeepAsIs = this.setKeepAsIs.bind(this);

		this.state = { keepAsIs: false };
	}

	componentDidMount() {
		if (this.props.copDetails) {
			this.props.setCopDetails(this.props.copDetails);

			const {
				copDetails: { accountNumber, sortCode },
				sessionCOPChecks,
			} = this.props;
			const sessionCOPChecksForThisSCAN =
				sessionCOPChecks && sessionCOPChecks[`${accountNumber}${sortCode.replace(/-/g, '')}`];
			const scanBlockedForThisSession = sessionCOPChecksForThisSCAN
				? sessionCOPChecksForThisSCAN.tryCount === 5
				: false;
			if (scanBlockedForThisSession) {
				this.props.setCopDetails({ page: pages.COP.Blocked });
			}
		}
	}

	componentDidUpdate(prevProps) {
		if (this.props.accountError !== prevProps.accountError) {
			this.props.setCopDetails({
				page: this.props.accountError ? pages.COP.CreateAccountError : pages.COP.MakeMatch,
			});
		}

		if (this.props.templateError !== prevProps.templateError && this.props.templateError) {
			this.props.setCopDetails({
				page: pages.COP.CreateTemplateError,
			});
		}

		// If we are on a different 'page'
		if (prevProps.page !== this.props.page) {
			const {
				copDetails: { accountNumber, sortCode },
				sessionCOPChecks,
			} = this.props;
			const sessionCOPChecksForThisSCAN =
				sessionCOPChecks && sessionCOPChecks[`${accountNumber}${sortCode.replace(/-/g, '')}`];
			const tryCount = sessionCOPChecksForThisSCAN ? sessionCOPChecksForThisSCAN.tryCount : 1;

			const blockedPage = returnBlockedPage(COP_MAX_TRY_COUNT, tryCount);
			const reasonCode = get(this.props, 'copResult.result.cop_reason_code', null);

			const hasNotMatched = checkIfMatchFailed(blockedPage, prevProps.page, reasonCode);
			if (hasNotMatched) {
				this.props.setCopDetails({ page: blockedPage });
			}
		}
	}

	setKeepAsIs(keepAsIs) {
		this.setState({
			keepAsIs: keepAsIs,
		});
	}

	render() {
		const {
			accountError,
			copCheckTriggered,
			page,
			handleCancel,
			handleComplete,
			handleKeepAsIs,
			handleEdit,
			copResult,
			copDetails,
			payeeId,
			isPayeeSaved,
			isSingleUse,
			sessionCOPChecks,
			templateError,
		} = this.props;

		const { accountNumber, sortCode } = copDetails;

		const sessionCOPChecksForThisSCAN =
			sessionCOPChecks && sortCode && sessionCOPChecks[`${accountNumber}${sortCode.replace(/-/g, '')}`];
		const tryCount = sessionCOPChecksForThisSCAN ? sessionCOPChecksForThisSCAN.tryCount : 1;

		const scanDetails = {
			accountNumber,
			sortCode,
		};

		switch (page) {
			case pages.COP.MakeMatch:
				return (
					<MakeMatch
						copCheckTriggered={copCheckTriggered}
						copDetails={copDetails}
						handleCancel={handleCancel}
						tryCount={tryCount}
					/>
				);
			case pages.COP.CloseMatch:
				return (
					<CloseMatch
						handleCancel={handleCancel}
						handleComplete={handleComplete}
						copResult={copResult}
						setKeepAsIs={this.setKeepAsIs}
					/>
				);
			case pages.COP.MatchMade:
				return (
					<MatchMade
						isPayeeSaved={isPayeeSaved}
						isSingleUse={isSingleUse}
						handleComplete={handleComplete}
						copResult={copResult}
						scanDetails={scanDetails}
					/>
				);
			case pages.COP.AccountWarning:
				return (
					<AccountWarning
						handleCancel={handleCancel}
						handleComplete={handleComplete}
						handleKeepAsIs={handleKeepAsIs}
						copResult={copResult}
						copDetails={copDetails}
						keepAsIs={this.state.keepAsIs}
					/>
				);
			case pages.COP.NoMatch:
				return (
					<NoMatch
						handleCancel={handleCancel}
						handleEdit={handleEdit}
						copResult={copResult}
						payeeId={payeeId}
						setKeepAsIs={this.setKeepAsIs}
					/>
				);
			case pages.COP.Blocked:
				return <Blocked handleCancel={handleCancel} copDetails={copDetails} setKeepAsIs={this.setKeepAsIs} />;
			case pages.COP.ServiceDown:
				return (
					<ServiceDown
						handleCancel={handleCancel}
						handleEdit={handleEdit}
						handleComplete={handleComplete}
						payeeId={payeeId}
						copDetails={copDetails}
						setKeepAsIs={this.setKeepAsIs}
					/>
				);
			case pages.COP.CreateAccountError:
				return <CreateAccountError onPrimary={handleEdit} errorDetails={accountError} tryCount={tryCount} />;
			case pages.COP.CreateTemplateError:
				return <CreateTemplateError onPrimary={handleCancel} errorDetails={templateError} />;
			default:
				return null;
		}
	}
}

LayoutContainer.propTypes = {
	accountError: PropTypes.shape({
		response: PropTypes.shape({
			coded_exceptions: PropTypes.arrayOf(
				PropTypes.shape({
					error_status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
				})
			),
		}),
	}),
	copCheckTriggered: PropTypes.func,
	page: PropTypes.string.isRequired,
	copDetails: PropTypes.shape(copPropTypes),
	setCopDetails: PropTypes.func,
	handleComplete: PropTypes.func,
	handleCancel: PropTypes.func,
	handleEdit: PropTypes.func,
	isPayeeSaved: PropTypes.bool,
	isSingleUse: PropTypes.bool,
	sessionCOPChecks: PropTypes.object.isRequired,
	templateError: PropTypes.shape({
		response: PropTypes.shape({
			coded_exceptions: PropTypes.arrayOf(
				PropTypes.shape({
					error_status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
				})
			),
		}),
	}),
};

function mapStateToProps(state) {
	return {
		page: getCopPage(state),
		copResult: getCopResult(state),
		accountError: getAccountError(state),
		templateError: getTemplateError(state),
	};
}

export default connect(mapStateToProps, { setCopDetails })(LayoutContainer);
